import type { ReactNode } from 'react';

import PageHeader from './PageHeader';
import KpiTile, { type KpiTileSize, type KpiTileTone } from './KpiTile';

/**
 * RECEPTION-FIX-AND-POLISH-BATCH-V1 -- THE SUMMARY HEADER (UX LAW 4:
 * SUMMARY FIRST; founder-ratified 2026-08-03).
 *
 * Every major surface OPENS with a ten-second summary answering the role's
 * questions; details live one click / one scroll below, never the opening
 * view. This component is that opening, composed ENTIRELY from the frozen
 * canon (no new visual vocabulary): the ONE PageHeader chrome on top, a
 * KpiTile strip underneath, and AT MOST ONE page-level primary action
 * (DENSITY rule 8 / S-2 -- the `action` slot renders in the header's
 * actions cluster; screens with per-row actions pass none).
 *
 * Tiles are FACTS, not teaching (DENSITY rules 2/3): value hero, label
 * micro, sub names the state in user words. An unproven figure renders
 * value '-' with the truth named in `sub` -- never a fake zero (rule 6).
 * Tone carries urgency (the existing five-tone tile law); an all-calm
 * strip looks quiet on purpose.
 */

export interface SummaryTileDef {
  readonly key: string;
  readonly label: string;
  readonly value: ReactNode;
  readonly sub?: ReactNode;
  readonly hint?: string;
  readonly tone?: KpiTileTone;
  readonly onClick?: () => void;
  readonly active?: boolean;
  readonly testId?: string;
}

export interface SummaryHeaderProps {
  readonly eyebrow?: ReactNode;
  readonly title: ReactNode;
  /** Falsy entries are skipped (the PageHeader contract). */
  readonly subs?: ReadonlyArray<ReactNode>;
  /** THE one page-level primary action (or none). */
  readonly action?: ReactNode;
  /** SCOPE slot, forwarded verbatim to PageHeader's registered `scope`
   *  ("clinic / all clinics chip"). 119-CLINIC-OPERATING-SPINE-V1: money and
   *  the other summary-header surfaces must name the clinic they are showing,
   *  and they reach the same slot every PageHeader surface uses rather than
   *  growing a second place to put it. */
  readonly scope?: ReactNode;
  readonly tiles: readonly SummaryTileDef[];
  /** 'hero' for money/headline pages; 'base' for operational strips. */
  readonly tileSize?: KpiTileSize;
  readonly testId?: string;
}

export default function SummaryHeader({
  eyebrow,
  title,
  subs,
  action,
  scope,
  tiles,
  tileSize = 'base',
  testId,
}: SummaryHeaderProps) {
  return (
    <div className="ac-summary" data-testid={testId}>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        subs={subs}
        actions={action}
        scope={scope}
      />
      {tiles.length > 0 ? (
        <div className="ac-summary__tiles" role="group" aria-label="Summary">
          {tiles.map((t) => (
            <KpiTile
              key={t.key}
              label={t.label}
              value={t.value}
              sub={t.sub}
              hint={t.hint}
              tone={t.tone}
              size={tileSize}
              onClick={t.onClick}
              active={t.active}
              testId={t.testId}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
